import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  CalendarViewWeek as CalendarViewWeekIcon,
  ChevronLeft,
  ChevronRight,
  Person,
  ViewHeadline as ViewHeadlineIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomeDataGrid from "../../../components/common/CustomeDataGrid";
import apiService from "../../../service/Api";
import { RootState } from "../../../store/Index";
import type { GridRenderCellParams } from "@mui/x-data-grid";

interface RequestedQuote {
  id: number;
  event_date: string;
  event_time: string;
  venue: string;
  deceased_name: string;
  deceased_photo?: string;
  status?: string;
  request_type:string
 
}

interface IColumn {
  id: string;
  title: string;
  description: string;
  //count: number;
  items: RequestedQuote[];
  collapsed?: boolean;
}

interface QuoteRequestResponse {
  id: number;
  event_datetime: string;
  venue_name?: string | null;
  request_type:string
  event:{
    title:string
  }
  deceased?: {
    first_name?: string | null;
    surname?: string | null;
    person_image?: string | null;
  } | null;
}

const SortableCard = React.memo(
  ({ item, isDragging }: { item: RequestedQuote; isDragging: boolean }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: item.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
    };

    const navigate = useNavigate();
    const reduxTheme = useSelector((state: RootState) => state.theme);

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <Card
          onClick={() => navigate(`/request-for-quote/${item.id}`)}
          sx={{
            mb: 2,
            cursor: "grab",
            bgcolor: reduxTheme.primary_colors[800],
            borderRadius: 3,
            boxShadow: 0,
            overflow: "hidden",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Avatar
                src={item.deceased_photo || undefined}
                sx={{
                  width: 36,
                  height: 36,
                  border: `1px solid ${reduxTheme.primary_colors[900]}`,
                  flexShrink: 0,
                }}
              >
                {!item.deceased_photo && <Person fontSize="small" />}
              </Avatar>

              <Box flex={1} minWidth={0}>
                <Typography variant="body1" fontWeight="bold" noWrap>
                  {item.deceased_name}
                </Typography>
              </Box>
            </Box>

            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {item.event_date} at {item.event_time}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {item.venue}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </div>
    );
  },
);

SortableCard.displayName = "SortableCard";

// Kanban Column
const KanbanColumn = React.memo(
  ({
    column,
    activeId,
    onToggle,
  }: {
    column: IColumn;
    activeId: number | null;
    onToggle: (id: string) => void;
  }) => {
    const hasItems = column.items.length > 0;

    const { setNodeRef, isOver } = useDroppable({
      id: column.id,
    });

    return (
      <Paper
        ref={setNodeRef}
        component={motion.div}
        animate={{ width: column.collapsed ? 64 : 300 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        sx={{
          p: column.collapsed ? 1 : 2,
          overflow: "hidden",
          minHeight: 240,
          //minWidth: 320,
          //maxWidth: 380,
          //width: "100%",
          boxShadow: 0,
          bgcolor: isOver ? "action.hover" : "background.paper",
          borderRadius: 3,
          display: "flex",
          flexDirection: "column",
          //transition: "background-color 0.2s ease",
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 1 }}>
          {!column.collapsed && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Tooltip title="Collapse column">
                  <Box
                    component={motion.div}
                    onClick={() => onToggle(column.id)}
                    whileHover={{ scale: 1.15 }}
                    whileTap={{ scale: 0.9 }}
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      flexShrink: 0,
                    }}
                  >
                    <ChevronLeft sx={{ fontSize: 16, color: "#fff" }} />
                  </Box>
                </Tooltip>

                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {column.title}
                </Typography>
              </Box>

              <Chip label={column.items.length} size="small" color="primary" />
            </Box>
          )}

          {column.collapsed && (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Tooltip title="Expand column">
                <Box
                  component={motion.div}
                  onClick={() => onToggle(column.id)}
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    bgcolor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    mb: 1,
                  }}
                >
                  <ChevronRight sx={{ fontSize: 16, color: "#fff" }} />
                </Box>
              </Tooltip>

              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  writingMode: "vertical-rl",
                  transform: "rotate(180deg)",
                  pointerEvents: "none",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  letterSpacing={1}
                  color="text.secondary"
                  whiteSpace="nowrap"
                >
                  {column.title.toUpperCase()}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {!column.collapsed && (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {column.description}
            </Typography>

            <Box
              sx={{
                flexGrow: 1,
                minHeight: hasItems ? "auto" : 140,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <SortableContext
                items={column.items.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                {column.items.map((item) => (
                  <SortableCard
                    key={item.id}
                    item={item}
                    isDragging={activeId === item.id}
                  />
                ))}
              </SortableContext>

              {!hasItems && (
                <Box
                  sx={{
                    flexGrow: 1,
                    border: `2px dashed`,
                    borderColor: isOver ? "primary.main" : "grey.400",
                    borderRadius: 3,
                    minHeight: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mt: 2,
                    bgcolor: "background.paper",
                    transition: "border-color 0.2s ease",
                  }}
                >
                  <Typography
                    variant="body2"
                    color={isOver ? "primary.main" : "text.secondary"}
                  >
                    {isOver ? "Drop here" : "Drop items here"}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Paper>
    );
  },
);

KanbanColumn.displayName = "KanbanColumn";

const RequestedQuotesDashboard: React.FC = () => {
  const [viewMode, setViewMode] = useState<"table" | "kanban">("table");
  const kanbanScrollRef = useRef<HTMLDivElement | null>(null);
  const lastDeltaXRef = useRef(0);

  const COLUMN_WIDTH = 300;
  const COLUMN_GAP = 24;
  const SNAP_SIZE = COLUMN_WIDTH + COLUMN_GAP;

  const snapToColumn = (index: number) => {
    const container = kanbanScrollRef.current;
    if (!container) return;

    const maxScrollLeft = container.scrollWidth - container.clientWidth;

    let target = index * SNAP_SIZE;

    target = Math.max(0, Math.min(target, maxScrollLeft));

    container.scrollTo({
      left: target,
      behavior: "smooth",
    });
  };

  const [columns, setColumns] = useState<IColumn[]>([
    {
      id: "enquiry",
      title: "Enquiry",
      description: "Initial client inquiries received",
      items: [],
    },
    {
      id: "quote",
      title: "Quote",
      description: "Quote prepared and sent to client",
      items: [],
    },
    {
      id: "approved_booking",
      title: "Approved Booking",
      description: "Client has approved and booking is confirmed",
      items: [],
    },
    {
      id: "rejected",
      title: "Rejected",
      description: "Client declined the quote",
      items: [],
    },
    {
      id: "ops",
      title: "OPS",
      description: "Operations team is handling the service",
      items: [],
    },
    {
      id: "fulfilled",
      title: "Fulfilled",
      description: "Service has been successfully completed",
      items: [],
    },
    {
      id: "sales_post_event",
      title: "Sales for Post Event Care",
      description: "Follow-up sales opportunities for post-event care",
      items: [],
    },
  ]);

  const [tableRows, setTableRows] = useState<RequestedQuote[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<number | null>(null);
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const toggleColumn = (columnId: string) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.id === columnId ? { ...col, collapsed: !col.collapsed } : col,
      ),
    );
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragMove = ({ delta }: { delta: { x: number } }) => {
    const container = kanbanScrollRef.current;
    if (!container) return;

    const diffX = delta.x - lastDeltaXRef.current;

    if (Math.abs(diffX) > 1) {
      const scrollSensitivity = 0.6;
      container.scrollLeft += diffX * scrollSensitivity;
    }

    lastDeltaXRef.current = delta.x;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as number);
    lastDeltaXRef.current = 0;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    lastDeltaXRef.current = 0;

    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    setColumns((prev) => {
      const newColumns = [...prev];
      const sourceCol = newColumns.find((c) =>
        c.items.some((i) => i.id === active.id),
      );
      const resolvedDestCol =
        newColumns.find(
          (c) => c.items.some((i) => i.id === over.id) || c.id === over.id,
        ) ?? newColumns.find((c) => c.id === over.id);
      if (!sourceCol || !resolvedDestCol) {
        setActiveId(null);
        return prev;
      }

      const destCol = resolvedDestCol;
      const destColumnId = destCol.id;
      const itemIndex = sourceCol.items.findIndex((i) => i.id === active.id);
      if (itemIndex === -1) {
        setActiveId(null);
        return prev;
      }

      const movedItem = {
        ...sourceCol.items[itemIndex],
        status: destColumnId,
      };

      sourceCol.items.splice(itemIndex, 1);

      const overIndex = destCol.items.findIndex((i) => i.id === over.id);
      if (overIndex === -1) {
        destCol.items.push(movedItem);
      } else {
        destCol.items.splice(overIndex, 0, movedItem);
      }
      const destIndex = newColumns.findIndex((c) => c.id === destColumnId);

      requestAnimationFrame(() => {
        snapToColumn(destIndex);
      });

      // setTimeout(() => {
      //   snapToColumn(destIndex);
      // }, 50);

      // setTimeout(() => {
      //   setActiveId(null);
      // }, 350);

      return newColumns;
    });
  };

  const handleDragCancel = () => {
    setActiveId(null);
    lastDeltaXRef.current = 0;
  };

  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const response = await apiService.get<QuoteRequestResponse[]>(
          "/api/bookings/quote-request/",
        );

        const formatted: RequestedQuote[] = response.data.map((item) => {
          const dt = new Date(item.event_datetime);

          return {
            id: item.id,
            event_date: dt.toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            }),
            event_time: dt.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            venue: item.venue_name || "Not specified",
            deceased_name: item.request_type === "SPONSORSHIP"
              ? item.event.title
              : `${item.deceased?.first_name} ${item.deceased?.surname}`,
            deceased_photo: item.deceased?.person_image || "",
            status: "enquiry",
            request_type:item.request_type,
           
          };
        });

        setTableRows(formatted);
      } catch (err) {
        console.error("Failed to fetch quotes", err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuotes();
  }, []);

  useEffect(() => {
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        items: tableRows.filter((q) => q.status === col.id),
      })),
    );
  }, [tableRows]);

  const tableColumns = [
    { field: "id", headerName: "ID", width: 80 },
    {
      field: "deceased_photo",
      headerName: "Photo",
      width: 90,
      renderCell: (params: GridRenderCellParams<RequestedQuote>) => (
        <Avatar
          src={params.value}
          alt={params.row.deceased_name}
          sx={{ width: 40, height: 40, mt: 1 }}
        />
      ),
    },
    { field: "deceased_name", headerName: "Name", flex: 1 },
    { field: "event_date", headerName: "Date of Service", flex: 1 },
    { field: "event_time", headerName: "Start Time", flex: 1 },
    { field: "venue", headerName: "Venue", flex: 1 },
  ];

  if (loading) return <Typography>Loading requested quotes...</Typography>;

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Grid
        container
        alignItems="center"
        justifyContent="flex-end"
        sx={{ mb: 3 }}
      >
        <Button
          variant="contained"
          sx={{
            mr: 1,
            bgcolor:
              viewMode === "table" ? "primary" : reduxTheme.primary_colors[800],
          }}
          onClick={() => setViewMode("table")}
        >
          <ViewHeadlineIcon
            sx={{
              color:
                viewMode === "table"
                  ? reduxTheme.primary_colors[900]
                  : reduxTheme.primary_colors[700],
            }}
          />
          List
        </Button>
        <Button
          variant="contained"
          onClick={() => setViewMode("kanban")}
          sx={{
            bgcolor:
              viewMode === "kanban"
                ? "primary"
                : reduxTheme.primary_colors[800],
          }}
        >
          <CalendarViewWeekIcon
            sx={{
              color:
                viewMode === "kanban"
                  ? reduxTheme.primary_colors[900]
                  : reduxTheme.primary_colors[700],
            }}
          />
          <Typography sx={{ color: "black" }}> Kanban</Typography>
        </Button>
      </Grid>

      {viewMode === "table" && (
        <Card sx={{ boxShadow: 0 }}>
          <CardContent>
            <CustomeDataGrid
              rows={tableRows}
              columns={tableColumns}
              onRowClick={(params) =>
                navigate(`/request-for-quote/${params.row.id}`)
              }
            />
          </CardContent>
        </Card>
      )}

      {viewMode === "kanban" && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <Box
            ref={kanbanScrollRef}
            display="flex"
            gap={3}
            sx={{ overflowX: "auto", pb: 4 }}
          >
            {columns.map((column) => (
              <motion.div
                key={column.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <KanbanColumn
                  column={column}
                  activeId={activeId}
                  onToggle={toggleColumn}
                />
              </motion.div>
            ))}
          </Box>

          {/* Drag Overlay */}
          <DragOverlay>
            {activeId ? (
              <motion.div
                initial={{ scale: 0.9, rotate: -5, opacity: 0 }}
                animate={{ scale: 0.8, rotate: 5, opacity: 1 }}
                exit={{ scale: 0.9, rotate: -5, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Card
                  sx={{
                    width: 300,
                    cursor: "grabbing",
                    bgcolor: reduxTheme.primary_colors[800],
                    borderRadius: 3,
                    boxShadow: 20,
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    {(() => {
                      const activeItem = columns
                        .flatMap((col) => col.items)
                        .find((i) => i.id === activeId);
                      if (!activeItem) return null;

                      return (
                        <>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={2}
                            mb={1}
                          >
                            <Avatar
                              src={activeItem.deceased_photo || undefined}
                              sx={{
                                width: 36,
                                height: 36,
                                border: `1px solid ${reduxTheme.primary_colors[900]}`,
                                flexShrink: 0,
                              }}
                            >
                              {!activeItem.deceased_photo && (
                                <Person fontSize="small" />
                              )}
                            </Avatar>

                            <Box flex={1} minWidth={0}>
                              <Typography
                                variant="body1"
                                fontWeight="bold"
                                noWrap
                              >
                                {activeItem.deceased_name}
                              </Typography>
                            </Box>
                          </Box>

                          <Box display="flex" flexDirection="column" gap={0.5}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {activeItem.event_date} at {activeItem.event_time}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {activeItem.venue}
                            </Typography>
                          </Box>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              </motion.div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}
    </Box>
  );
};

export default RequestedQuotesDashboard;
